import { PlacedBlock, IsoPoint, Dimension } from '@/app/types/cad';

/**
 * @deprecated INCORRECT: This function uses x/y/z coordinates that don't exist on IsoPoint (only u/v exist)
 * Calculate the isometric distance between two points
 */
export function calculateIsoDistance(p1: IsoPoint, p2: IsoPoint): number {
  const dx = Math.abs(p2.x - p1.x);
  const dy = Math.abs(p2.y - p1.y);
  const dz = Math.abs(p2.z - p1.z);
  
  // Isometric distance in grid units
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * @deprecated INCORRECT: Uses calculateIsoDistance which is broken. Use dimension-based length instead.
 * Calculate the total length of all lines in a block
 * @param block - The block to calculate length for
 * @param gridSizeMm - Size of one grid unit in mm (default: 200mm)
 * @returns Total length in mm
 */
export function calculateBlockLength(block: PlacedBlock, gridSizeMm: number = 200): number {
  let totalLengthInGridUnits = 0;
  
  // Check if block has lines
  const lines = block.blockData.elements.filter(el => el.type === 'line');
  
  if (lines.length === 0) {
    return 0;
  }
  
  // Calculate length for each line
  lines.forEach(line => {
    if (line.type === 'line') {
      const distance = calculateIsoDistance(line.start, line.end);
      totalLengthInGridUnits += distance;
    }
  });
  
  // Convert grid units to mm
  return totalLengthInGridUnits * gridSizeMm;
}

/**
 * Check if a block is a line component (has at least one line element)
 */
export function isLineComponent(block: PlacedBlock): boolean {
  return block.blockData.elements.some(el => el.type === 'line');
}

/**
 * Update the "Länge" attribute for a block based on its dimension elements
 * IMPORTANT: This function extracts length from DIMENSION ELEMENTS, not from geometry!
 * 
 * Logic:
 * 1. Find all dimension elements (type === 'dimension', NOT 'angle-dimension')
 * 2. For each dimension: use actualValue (user override) if exists, otherwise use value
 * 3. Sum all dimension values
 * 4. If sum > 0: write to "Länge" attribute
 * 5. If no dimensions: remove "Länge" attribute if it exists
 * 
 * @param block - The block to update
 * @returns Updated block with "Länge" attribute set correctly
 */
export function updateLengthAttribute(block: PlacedBlock, gridSizeMm: number = 200): PlacedBlock {
  // Find ALL dimension elements (NOT angle-dimension!)
  const dimensions = block.blockData.elements.filter(
    el => el.type === 'dimension'
  ) as Dimension[];
  
  // If no dimensions exist, remove "Länge" attribute if present
  if (dimensions.length === 0) {
    const attributes = block.blockData.attributes || [];
    const lengthAttrIndex = attributes.findIndex(attr => attr.name === 'Länge');
    
    if (lengthAttrIndex !== -1) {
      console.log(`🗑️ Block ${block.id}: Removed "Länge" attribute (no dimensions found)`);
      attributes.splice(lengthAttrIndex, 1);
      
      return {
        ...block,
        blockData: {
          ...block.blockData,
          attributes,
        },
      };
    }
    
    return block;
  }
  
  // Extract and sum all dimension values
  let totalLength = 0;
  let unit = 'mm'; // Default unit
  const dimensionValues: number[] = [];
  
  dimensions.forEach((dimension, index) => {
    // Use actualValue (user override) if exists, otherwise use value
    const valueStr = dimension.actualValue || dimension.value;
    
    // Parse to number
    const value = parseFloat(valueStr);
    
    if (!isNaN(value) && value > 0) {
      dimensionValues.push(value);
      totalLength += value;
      
      // Use unit from first valid dimension
      if (index === 0 && dimension.unit) {
        unit = dimension.unit;
      }
    } else {
      console.warn(`⚠️ Block ${block.id}: Invalid dimension value "${valueStr}" (skipped)`);
    }
  });
  
  // If no valid dimensions found, remove attribute
  if (totalLength === 0) {
    const attributes = block.blockData.attributes || [];
    const lengthAttrIndex = attributes.findIndex(attr => attr.name === 'Länge');
    
    if (lengthAttrIndex !== -1) {
      attributes.splice(lengthAttrIndex, 1);
      console.log(`🗑️ Block ${block.id}: Removed "Länge" attribute (no valid dimension values)`);
      
      return {
        ...block,
        blockData: {
          ...block.blockData,
          attributes,
        },
      };
    }
    
    return block;
  }
  
  // Round to 2 decimal places
  const roundedLength = Math.round(totalLength);
  
  // Silent calculation - don't spam console
  
  // Update or add "Länge" attribute
  const attributes = block.blockData.attributes || [];
  const lengthAttrIndex = attributes.findIndex(attr => attr.name === 'Länge');
  
  if (lengthAttrIndex !== -1) {
    // Update existing attribute
    attributes[lengthAttrIndex] = {
      ...attributes[lengthAttrIndex],
      value: roundedLength,
      unit: unit,
      type: 'length',
      readonly: false, // User can edit this in the table
    };
  } else {
    // Add new attribute
    attributes.push({
      id: `attr-length-${Date.now()}`,
      name: 'Länge',
      value: roundedLength,
      unit: unit,
      type: 'length',
      readonly: false,
    });
  }
  
  return {
    ...block,
    blockData: {
      ...block.blockData,
      attributes,
    },
  };
}

/**
 * Update length attributes for all blocks in array
 * @param blocks - Array of blocks to update
 * @param gridSizeMm - (Deprecated, kept for backwards compatibility)
 */
export function updateAllLengthAttributes(blocks: PlacedBlock[], gridSizeMm: number = 200): PlacedBlock[] {
  return blocks.map(block => updateLengthAttribute(block, gridSizeMm));
}

/**
 * @deprecated Use updateLengthAttribute() instead
 * Extract length values from dimension elements in blocks
 * Returns BOTH calculated and overridden values
 */
export function extractLengthFromDimensions(block: PlacedBlock): { 
  calculated: number | null; 
  overridden: number | null;
} {
  // Find dimension elements in the block
  const dimensions = block.blockData.elements.filter(el => el.type === 'dimension');
  
  if (dimensions.length === 0) {
    return { calculated: null, overridden: null };
  }
  
  // Get the first dimension element (usually the main length dimension)
  const mainDimension = dimensions[0] as any;
  
  // Extract calculated value
  let calculated: number | null = null;
  if (mainDimension.value) {
    const value = parseFloat(mainDimension.value);
    if (!isNaN(value)) {
      calculated = value;
    }
  }
  
  // Extract overridden value (stored as "actualValue" - see DimensionEditDialog.tsx)
  let overridden: number | null = null;
  if (mainDimension.actualValue && mainDimension.actualValue !== mainDimension.value) {
    const value = parseFloat(mainDimension.actualValue);
    if (!isNaN(value)) {
      overridden = value;
    }
  }
  
  return { calculated, overridden };
}

/**
 * @deprecated Use updateAllLengthAttributes() instead
 * Restore length attributes from dimension elements
 * This recovers true length values when they were lost
 * Creates TWO attributes: "Länge" (calculated) and "Länge überschrieben" (user override)
 */
export function restoreLengthAttributesFromDimensions(blocks: PlacedBlock[]): PlacedBlock[] {
  console.warn('⚠️ restoreLengthAttributesFromDimensions() is deprecated, using updateAllLengthAttributes() instead');
  return updateAllLengthAttributes(blocks);
}